/**
 * Script Editor — a true, LIVING Final Draft page (Agent 02).
 *
 * Not a scene-card form: one continuous, typeset screenplay you write in
 * like Final Draft. Highlights:
 *
 *   - PURE FREE-TYPE. No "+ add scene" front door (a subtle hook remains
 *     for the Koko bridge / timeline). Type a slug to start a scene;
 *     Enter drops to the contextual next element type; the per-line chip
 *     (and Tab) sets a line's type explicitly. Empty start = a blank page
 *     you can type into, with an inline "drop a PDF" import.
 *   - @-REFERENCE SYSTEM (owned here, _GLOBAL.md G1). Type `@` → an
 *     autocomplete of existing entities + "create new"; refs render as
 *     clickable pills and feed the store's ref→scene index.
 *   - LIVING DOC (G6). A new character cue, a new slug location, or a new
 *     `@ref` auto-creates the entity in the store — whether the user or
 *     the Koko agent typed it.
 *   - CLICK → DRAWER (G6). Clicking any inline circle / location square /
 *     ref pill opens that entity's edit form in the shared drawer, in
 *     place — no navigation.
 *   - PER-CHARACTER ▶ play uses the character's VoiceProfile + `/tts`;
 *     per-DIALOGUE sound editing (delivery / tone / speed) inline (G8).
 *   - COMMENTS (Google-Docs style): select a line → comment; anchored
 *     margin threads; resolve / reopen / delete; persisted; Koko can
 *     comment too.
 *
 * Canonical content is `StudioScene.elements[]`; `body` stays mirrored
 * for back-compat. Comments + per-line sound persist in sidecar stores
 * (see scriptSidecars.ts) until the integrator folds them into the store.
 */

import { BasePanel } from "./BasePanel";
import { buildHelperParagraph, buildPixelBox, buildStudioButton } from "./dom";
import type {
  ScriptElement,
  StudioAsset,
  StudioCharacter,
  StudioLocation,
  StudioScene,
  StudioStore,
} from "../StudioStore";
import {
  bodyToElements,
  classifyType,
  cycleType,
  elementsToBody,
  extractRefTokens,
  findCharacterByName,
  makeElementId,
  nextTypeOnEnter,
  parseSlug,
  placeholderFor,
  resolveSceneElements,
  typeLabel,
  type ElementType,
} from "./scriptModel";
import {
  analyzeScript,
  clampText,
  countParsedScenes,
  extractPdfText,
  formatBytes,
  ingestParsedIntoStore,
  looksLikePdf,
  MAX_FILE_BYTES,
  resolveWorkerBaseUrl,
  toUserMessage,
} from "./scriptIngest";
import {
  DEFAULT_LINE_SOUND,
  LineSoundStore,
  ScriptCommentStore,
  ScriptFormatStore,
  type Delivery,
  type LineSound,
  type ScriptComment,
} from "./scriptSidecars";
import { LineVoicePlayer, resolveVoice } from "./scriptVoice";
import { buildPixelIdenticonImage } from "../shared/pixelIdenticon";
import { openEntityDrawer, closeEntityDrawer, openImageEditor } from "../shared";
// Cross-panel reuse (G6): the in-script click→drawer renders the SAME
// edit forms the entity panels use, not bespoke stubs.
import { CharacterDetailForm } from "./CharacterCreatorPanel";
import { buildAssetEditForm } from "./AssetsPanel";
import "../styles/script-editor.css";

interface ScriptEditorPanelOptions {
  store: StudioStore;
  /** Worker base URL — used for the `/tts` route on per-line playback. */
  workerBaseUrl: string;
}

type EntityKind = "character" | "location" | "asset" | "look";

interface EntityIndexEntry {
  type: EntityKind;
  id: string;
  name: string;
}

interface RefMenuState {
  mode: "ref" | "smart";
  textEl: HTMLElement;
  scene: StudioScene;
  index: number;
  /** Offset of the `@` in the line text. */
  tokenStart: number;
  query: string;
  items: RefMenuItem[];
  selected: number;
}

interface RefMenuItem {
  label: string;
  hint: string;
  /** Existing entity, or null for a "create new" action. */
  entry: EntityIndexEntry | null;
  createKind?: EntityKind;
  name: string;
}

interface CommentSelection {
  sceneId: string;
  elementId: string;
  start: number;
  end: number;
  quote: string;
  rect: DOMRect;
}

interface HistoryEntry {
  label: string;
  before: StudioScene[];
  after: StudioScene[];
}

export class ScriptEditorPanel extends BasePanel {
  private readonly store: StudioStore;
  private readonly commentStore = new ScriptCommentStore();
  private readonly lineSoundStore = new LineSoundStore();
  private readonly formatStore = new ScriptFormatStore();
  private readonly voicePlayer: LineVoicePlayer;

  private rootEl: HTMLDivElement | null = null;
  private pageEl: HTMLDivElement | null = null;
  private marginEl: HTMLDivElement | null = null;
  private refMenuEl: HTMLDivElement | null = null;
  private selectionButtonEl: HTMLButtonElement | null = null;

  private unsubscribers: (() => void)[] = [];
  private refMenu: RefMenuState | null = null;
  private focusedElementId: string | null = null;
  private showComments = false;
  private activeCommentId: string | null = null;
  private commentSelection: CommentSelection | null = null;
  private history: HistoryEntry[] = [];
  private historyIndex = -1;
  private restoringHistory = false;
  private ignoreBlurUntil = 0;
  private deletedSceneUndoTimer: number | null = null;
  /** Element ids whose type the user pinned via the chip / Tab. */
  private readonly pinnedTypes = new Set<string>();
  /** After a structural change, focus this (sceneId,index) on next render. */
  private pendingFocus: { sceneId: string; index: number } | null = null;

  constructor(options: ScriptEditorPanelOptions) {
    super();
    this.store = options.store;
    this.voicePlayer = new LineVoicePlayer(options.workerBaseUrl);
  }

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");
    root.className = "fd-root";
    this.rootEl = root;

    const box = buildPixelBox();

    // A quiet application toolbar; the paper itself remains free of persistent controls.
    const toolbar = document.createElement("div");
    toolbar.className = "fd-toolbar";
    const helper = buildHelperParagraph("Screenplay · ⌘1–6 elements · ⌘Enter page break · ⌘D dual dialogue");
    helper.classList.add("fd-toolbar-helper");
    toolbar.appendChild(helper);

    const undo = buildStudioButton("↶", { onClick: () => this.undo() });
    undo.classList.add("fd-history-button", "fd-undo");
    undo.title = "Undo (⌘Z)";
    undo.dataset.kokoAction = "script-undo";
    const redo = buildStudioButton("↷", { onClick: () => this.redo() });
    redo.classList.add("fd-history-button", "fd-redo");
    redo.title = "Redo (⇧⌘Z)";
    redo.dataset.kokoAction = "script-redo";
    toolbar.append(undo, redo);

    const commentsToggle = buildStudioButton("Comments off", {
      onClick: () => this.toggleComments(),
    });
    commentsToggle.classList.add("fd-comments-toggle");
    commentsToggle.dataset.kokoAction = "script-comments-toggle";
    toolbar.appendChild(commentsToggle);
    box.fill.appendChild(toolbar);

    // Layout: the page + the comments margin.
    const layout = document.createElement("div");
    layout.className = "fd-layout";

    const page = document.createElement("div");
    page.className = "fd-pages";
    page.dataset.kokoSurface = "script-page";
    layout.appendChild(page);
    this.pageEl = page;

    const margin = document.createElement("div");
    margin.className = "fd-margin";
    margin.dataset.kokoSurface = "script-comments";
    layout.appendChild(margin);
    this.marginEl = margin;

    box.fill.appendChild(layout);

    // Floating @-autocomplete menu (positioned on demand).
    const menu = document.createElement("div");
    menu.className = "fd-ref-menu";
    menu.hidden = true;
    menu.dataset.kokoSurface = "ref-menu";
    root.appendChild(menu);
    this.refMenuEl = menu;

    const selectionButton = document.createElement("button");
    selectionButton.type = "button";
    selectionButton.className = "fd-selection-comment";
    selectionButton.textContent = "+ Comment";
    selectionButton.hidden = true;
    selectionButton.dataset.kokoAction = "script-selection-comment";
    selectionButton.addEventListener("mousedown", (event) => event.preventDefault());
    selectionButton.addEventListener("click", () => this.openSelectionComposer());
    root.appendChild(selectionButton);
    this.selectionButtonEl = selectionButton;

    root.appendChild(box.outer);
    return root;
  }

  protected onMounted(): void {
    for (const key of ["scenes", "characters", "locations", "entityRefs"] as const) {
      this.unsubscribers.push(this.store.subscribe(key, () => this.render()));
    }
    this.unsubscribers.push(this.commentStore.subscribe(() => this.renderComments()));
    this.unsubscribers.push(this.lineSoundStore.subscribe(() => this.render()));
    this.unsubscribers.push(this.formatStore.subscribe(() => this.render()));

    const onKokoComment = (event: Event) => this.handleKokoComment(event as CustomEvent);
    window.addEventListener("koko:script-comment", onKokoComment);
    this.unsubscribers.push(() =>
      window.removeEventListener("koko:script-comment", onKokoComment),
    );

    const onDocClick = (event: MouseEvent) => {
      if (!this.refMenu) return;
      if (this.refMenuEl && this.refMenuEl.contains(event.target as Node)) return;
      this.hideRefMenu();
    };
    document.addEventListener("mousedown", onDocClick, true);
    this.unsubscribers.push(() =>
      document.removeEventListener("mousedown", onDocClick, true),
    );

    const onSelection = () => this.captureCommentSelection();
    document.addEventListener("selectionchange", onSelection);
    this.unsubscribers.push(() => document.removeEventListener("selectionchange", onSelection));

    const onGlobalKeydown = (event: KeyboardEvent) => {
      const modifier = event.metaKey || event.ctrlKey;
      if (!modifier || event.altKey) return;
      if (event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) this.redo();
        else this.undo();
        return;
      }
      const active = document.activeElement?.closest<HTMLElement>(".fd-text");
      const sceneId = active?.dataset.sceneId;
      const index = Number(active?.dataset.lineIndex ?? -1);
      const scene = this.store.get("scenes").find((item) => item.id === sceneId);
      if (active && scene && index >= 0) {
        if (/^[1-6]$/.test(event.key)) {
          const types: ElementType[] = [
            "slug",
            "action",
            "character",
            "dialogue",
            "parenthetical",
            "transition",
          ];
          event.preventDefault();
          event.stopPropagation();
          this.pinnedTypes.add(active.dataset.elementId ?? "");
          this.pendingFocus = { sceneId: scene.id, index };
          this.commitElement(scene, index, active.textContent ?? "", types[Number(event.key) - 1]);
          return;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
          this.pendingFocus = { sceneId: scene.id, index };
          const element = this.getWorkingElements(scene)[index];
          if (element) this.formatStore.togglePageBreak(element.id);
          return;
        }
        if (event.key.toLowerCase() === "d") {
          const cue = this.findDialogueCue(this.getWorkingElements(scene), index);
          if (cue) {
            event.preventDefault();
            event.stopPropagation();
            this.pendingFocus = { sceneId: scene.id, index };
            this.formatStore.toggleDual(cue.id);
          }
          return;
        }
      }
    };
    window.addEventListener("keydown", onGlobalKeydown, true);
    this.unsubscribers.push(() => window.removeEventListener("keydown", onGlobalKeydown, true));

    this.applyCommentsClass();
    this.render();
  }

  protected onShown(): void {
    this.render();
  }

  protected onHidden(): void {
    this.voicePlayer.stop();
    this.hideRefMenu();
    this.hideSelectionButton();
  }

  protected onBeforeDestroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
    this.voicePlayer.stop();
    if (this.deletedSceneUndoTimer !== null) window.clearTimeout(this.deletedSceneUndoTimer);
  }

  // ------------------------------------------------------------------
  // Entity index (for @-autocomplete + ref resolution)
  // ------------------------------------------------------------------

  private buildEntityIndex(): {
    list: EntityIndexEntry[];
    byName: Map<string, EntityIndexEntry>;
    byId: Map<string, EntityIndexEntry>;
  } {
    const list: EntityIndexEntry[] = [];
    for (const c of this.store.get("characters")) {
      list.push({ type: "character", id: c.id, name: c.name });
    }
    for (const l of this.store.get("locations")) {
      list.push({ type: "location", id: l.id, name: l.name });
    }
    for (const a of this.store.get("assets")) {
      list.push({ type: "asset", id: a.id, name: a.name });
    }
    const looksByChar = this.store.get("costumeLooksByCharacterId");
    for (const looks of Object.values(looksByChar)) {
      for (const look of looks) {
        if (look.note?.trim()) {
          list.push({ type: "look", id: look.id, name: look.note.trim() });
        }
      }
    }
    const byName = new Map<string, EntityIndexEntry>();
    const byId = new Map<string, EntityIndexEntry>();
    for (const entry of list) {
      const key = entry.name.trim().toLowerCase();
      if (key && !byName.has(key)) byName.set(key, entry);
      byId.set(entry.id, entry);
    }
    return { list, byName, byId };
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  private render(): void {
    if (!this.pageEl) return;
    this.pageEl.replaceChildren();
    const scenes = this.store.get("scenes");

    if (scenes.length === 0) {
      const { page, body } = this.createPage(1);
      body.appendChild(this.buildEmptyState());
      this.pageEl.appendChild(page);
      this.renderComments();
      this.applyPendingFocus();
      this.updateHistoryButtons();
      return;
    }

    const PAGE_UNITS = 48;
    let pageNumber = 0;
    let used = 0;
    let pageBody: HTMLElement | null = null;
    let pageNode: HTMLElement | null = null;
    let previousSceneId: string | null = null;
    const startPage = (continuedSceneId: string | null = null) => {
      pageNumber += 1;
      const created = this.createPage(pageNumber);
      pageNode = created.page;
      pageBody = created.body;
      used = continuedSceneId ? 2 : 0;
      if (continuedSceneId) {
        const continued = document.createElement("div");
        continued.className = "fd-continued fd-continued-top";
        continued.textContent = "CONTINUED:";
        created.body.appendChild(continued);
      }
      this.pageEl!.appendChild(created.page);
    };
    startPage();

    scenes.forEach((scene, sceneIndex) => {
      const elements = resolveSceneElements(scene, this.store.get("characters"));
      const normalized = elements.length
        ? elements
        : [{ id: makeElementId(), type: "action" as const, text: "", characterId: null }];
      let storyboardInserted = false;
      normalized.forEach((element, index) => {
        const explicitBreak = this.formatStore.hasPageBreak(element.id) && used > 0;
        const units = this.estimateLineUnits(element);
        if (explicitBreak || used + units > PAGE_UNITS) {
          if (previousSceneId === scene.id && pageBody) {
            const bottom = document.createElement("div");
            bottom.className = "fd-continued fd-continued-bottom";
            bottom.textContent = "(CONTINUED)";
            pageBody.appendChild(bottom);
            pageNode?.classList.add("has-continuation");
          }
          startPage(previousSceneId === scene.id ? scene.id : null);
        }
        const row = this.buildLineRow(scene, normalized, index, sceneIndex + 1);
        if (this.formatStore.hasPageBreak(element.id)) row.classList.add("has-page-break");
        pageBody!.appendChild(row);
        used += units;
        previousSceneId = scene.id;

        if (!storyboardInserted && element.type === "action" && this.hasStoryboard(scene)) {
          const stripUnits = 6;
          if (used + stripUnits > PAGE_UNITS) startPage(scene.id);
          pageBody!.appendChild(this.buildStoryboardStrip(scene, sceneIndex + 1));
          used += stripUnits;
          storyboardInserted = true;
        }
      });
    });

    this.renderComments();
    this.applyPendingFocus();
    this.updateHistoryButtons();
  }

  private createPage(pageNumber: number): { page: HTMLDivElement; body: HTMLDivElement } {
    const page = document.createElement("div");
    page.className = "fd-page";
    page.dataset.pageNumber = String(pageNumber);
    const number = document.createElement("div");
    number.className = "fd-page-number";
    number.textContent = `${pageNumber}.`;
    page.appendChild(number);
    const body = document.createElement("div");
    body.className = "fd-page-body";
    page.appendChild(body);
    return { page, body };
  }

  private estimateLineUnits(element: ScriptElement): number {
    const widths: Record<ElementType, number> = {
      slug: 60,
      action: 60,
      character: 36,
      dialogue: 35,
      parenthetical: 26,
      transition: 20,
    };
    const lines = Math.max(1, Math.ceil(Math.max(1, element.text.length) / widths[element.type]));
    if (element.type === "slug") return Math.max(3, lines + 1);
    if (element.type === "character") return Math.max(2, lines + 1);
    if (element.type === "dialogue") return lines;
    return lines + (element.type === "action" ? 1 : 0);
  }

  private hasStoryboard(scene: StudioScene): boolean {
    return (
      (this.store.get("storyboardFramesBySceneId")[scene.id] ?? []).some((frame) => !!frame.image) ||
      scene.storyboardReady ||
      scene.videoReady
    );
  }

  private buildEmptyState(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "fd-empty";

    const drop = document.createElement("div");
    drop.className = "fd-drop";
    drop.dataset.kokoAction = "script-inline-drop";
    const dropIcon = document.createElement("div");
    dropIcon.className = "fd-drop-icon";
    dropIcon.textContent = "▣";
    const dropLabel = document.createElement("div");
    dropLabel.className = "fd-drop-label";
    dropLabel.textContent = "Drop a screenplay PDF to import";
    const dropHint = document.createElement("div");
    dropHint.className = "fd-drop-hint";
    dropHint.textContent =
      "Koko reads it into Final Draft and fills your cast, locations, and props.";

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "application/pdf,.pdf";
    // NB: a dedicated class (not the shared `studio-hidden-file-input`) so
    // it doesn't collide with the Upload panel's input in tests/queries.
    fileInput.className = "fd-file-input";
    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (file) void this.handleInlineUpload(file);
    });
    const chooseButton = buildStudioButton("Choose PDF", {
      variant: "primary",
      onClick: () => fileInput.click(),
    });
    chooseButton.dataset.kokoAction = "script-inline-choose";

    drop.append(dropIcon, dropLabel, dropHint, chooseButton, fileInput);
    drop.addEventListener("dragover", (e) => {
      e.preventDefault();
      drop.classList.add("is-dragover");
    });
    drop.addEventListener("dragleave", () => drop.classList.remove("is-dragover"));
    drop.addEventListener("drop", (e) => {
      e.preventDefault();
      drop.classList.remove("is-dragover");
      const file = e.dataTransfer?.files?.[0];
      if (file) void this.handleInlineUpload(file);
    });
    wrap.appendChild(drop);

    const status = document.createElement("div");
    status.className = "fd-inline-status";
    status.hidden = true;
    wrap.appendChild(status);

    const orRow = document.createElement("div");
    orRow.className = "fd-or";
    orRow.textContent = "— or just start writing —";
    wrap.appendChild(orRow);

    // A single blank, editable line that becomes the first scene.
    const startLine = document.createElement("div");
    startLine.className = "fd-line fd-action fd-pending";
    const text = document.createElement("div");
    text.className = "fd-text";
    text.contentEditable = "true";
    text.spellcheck = false;
    text.dataset.placeholder = "INT. LOCATION - DAY  (or any action line)";
    text.dataset.kokoAction = "script-pending-line";
    // Guard so Enter (then the resulting blur) doesn't create two scenes.
    let consumed = false;
    const promote = (value: string) => {
      if (consumed) return;
      consumed = true;
      this.startNewScene(value);
    };
    text.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        promote((text.textContent ?? "").trim());
      }
    });
    text.addEventListener("blur", () => {
      const value = (text.textContent ?? "").trim();
      if (value) promote(value);
    });
    startLine.appendChild(text);
    wrap.appendChild(startLine);
    return wrap;
  }

  private buildLineRow(
    scene: StudioScene,
    elements: ScriptElement[],
    index: number,
    sceneNumber: number,
  ): HTMLElement {
    const el = elements[index];
    const row = document.createElement("div");
    row.className = `fd-line fd-${el.type}`;
    row.dataset.sceneId = scene.id;
    row.dataset.lineIndex = String(index);
    row.dataset.elementId = el.id;
    const dialogueCue = this.findDialogueCue(elements, index);
    if (dialogueCue && this.formatStore.isDual(dialogueCue.id)) {
      row.classList.add("is-dual-dialogue");
    }

    // Per-line type chip at the line start.
    row.appendChild(this.buildTypeChip(scene, elements, index));

    if (el.type === "slug") {
      const sceneNo = document.createElement("span");
      sceneNo.className = "fd-scene-no";
      sceneNo.textContent = String(sceneNumber);
      row.appendChild(sceneNo);
      const sceneNoRight = sceneNo.cloneNode(true) as HTMLElement;
      sceneNoRight.classList.add("is-right");
      row.appendChild(sceneNoRight);
      row.appendChild(this.buildSceneMenu(scene, sceneNumber));
      row.appendChild(this.buildLocationThumb(scene));
    }

    const character =
      el.type === "character" && el.characterId
        ? this.store.get("characters").find((c) => c.id === el.characterId) ?? null
        : null;
    if (el.type === "character" && character) {
      row.appendChild(this.buildCharacterCircle(character));
    }

    const text = document.createElement("div");
    text.className = "fd-text";
    text.contentEditable = "true";
    text.spellcheck = false;
    text.dataset.kokoAction = "script-line";
    text.dataset.sceneId = scene.id;
    text.dataset.lineIndex = String(index);
    text.dataset.elementId = el.id;
    text.dataset.placeholder = placeholderFor(el.type);
    this.renderTextDisplay(text, el);

    text.addEventListener("focus", () => this.onTextFocus(text, el));
    text.addEventListener("input", () => this.onTextInput(text, scene, index));
    text.addEventListener("blur", () => this.onTextBlur(text, scene, index));
    text.addEventListener("keydown", (event) =>
      this.onTextKeydown(event, text, scene, elements, index),
    );
    row.appendChild(text);

    // Dialogue: play (character voice) + sound edit.
    if (el.type === "dialogue") {
      const speaker = this.resolveSpeaker(elements, index);
      row.appendChild(this.buildPlayButton(el, speaker));
      row.appendChild(this.buildSoundButton(el, speaker));
    }

    const marker = this.buildCommentMarker(el);
    if (marker) row.appendChild(marker);

    return row;
  }

  private buildTypeChip(
    scene: StudioScene,
    elements: ScriptElement[],
    index: number,
  ): HTMLElement {
    const el = elements[index];
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "fd-line-type";
    chip.textContent = typeLabel(el.type);
    chip.title = `Line type: ${typeLabel(el.type)} — click or use ⌘1–6`;
    chip.dataset.kokoAction = "script-line-type";
    chip.dataset.elementId = el.id;
    chip.dataset.type = el.type;
    chip.addEventListener("mousedown", (e) => e.preventDefault());
    chip.addEventListener("click", () => {
      const next = cycleType(el.type);
      this.pinnedTypes.add(el.id);
      const liveText = chip.parentElement?.querySelector<HTMLElement>(".fd-text")?.textContent;
      this.setElementType(scene, index, next, liveText);
    });
    return chip;
  }

  private findDialogueCue(elements: ScriptElement[], index: number): ScriptElement | null {
    for (let cursor = index; cursor >= 0; cursor -= 1) {
      if (elements[cursor].type === "character") return elements[cursor];
      if (!["dialogue", "parenthetical"].includes(elements[cursor].type)) break;
    }
    return null;
  }

  private buildSceneMenu(scene: StudioScene, sceneNumber: number): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "fd-scene-menu";
    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "fd-scene-menu-trigger";
    trigger.textContent = "•••";
    trigger.title = `Scene ${sceneNumber} menu`;
    trigger.setAttribute("aria-label", `Scene ${sceneNumber} menu`);
    trigger.dataset.kokoAction = "scene-menu";
    trigger.dataset.id = scene.id;
    const popover = document.createElement("div");
    popover.className = "fd-scene-popover";
    popover.hidden = true;
    const add = document.createElement("button");
    add.type = "button";
    add.className = "fd-scene-menu-item";
    add.textContent = "Add scene at end";
    add.dataset.kokoAction = "scene-menu-add";
    add.addEventListener("click", () => {
      popover.hidden = true;
      this.startNewScene("", "slug");
    });
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "fd-scene-menu-item is-danger";
    remove.textContent = "Delete scene…";
    remove.dataset.kokoAction = "scene-delete-request";
    remove.addEventListener("click", () => {
      popover.replaceChildren();
      const message = document.createElement("p");
      message.textContent = `Delete scene ${sceneNumber}?`;
      const cancel = document.createElement("button");
      cancel.type = "button";
      cancel.className = "fd-scene-confirm-cancel";
      cancel.textContent = "Cancel";
      const confirm = document.createElement("button");
      confirm.type = "button";
      confirm.className = "fd-scene-confirm-delete";
      confirm.textContent = "Delete";
      confirm.dataset.kokoAction = "scene-delete-confirm";
      cancel.addEventListener("click", () => {
        popover.replaceChildren(add, remove);
        popover.hidden = true;
      });
      confirm.addEventListener("click", () => this.deleteSceneWithUndo(scene));
      popover.append(message, cancel, confirm);
      confirm.focus();
    });
    popover.append(add, remove);
    trigger.addEventListener("click", () => {
      if (popover.firstElementChild !== add) popover.replaceChildren(add, remove);
      popover.hidden = !popover.hidden;
      if (!popover.hidden) add.focus();
    });
    wrap.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        popover.hidden = true;
        trigger.focus();
      }
    });
    wrap.addEventListener("focusout", () => {
      requestAnimationFrame(() => {
        if (!wrap.contains(document.activeElement)) popover.hidden = true;
      });
    });
    wrap.append(trigger, popover);
    return wrap;
  }

  private deleteSceneWithUndo(scene: StudioScene): void {
    this.runHistoryAction("Delete scene", () => this.store.deleteScene(scene.id));
    this.showUndoToast("Scene deleted");
  }

  private showUndoToast(message: string): void {
    if (!this.rootEl) return;
    this.rootEl.querySelector(".fd-undo-toast")?.remove();
    if (this.deletedSceneUndoTimer !== null) window.clearTimeout(this.deletedSceneUndoTimer);
    const toast = document.createElement("div");
    toast.className = "fd-undo-toast";
    const label = document.createElement("span");
    label.textContent = message;
    const undo = document.createElement("button");
    undo.type = "button";
    undo.textContent = "Undo";
    undo.dataset.kokoAction = "scene-delete-undo";
    undo.addEventListener("click", () => {
      this.undo();
      toast.remove();
    });
    toast.append(label, undo);
    this.rootEl.appendChild(toast);
    this.deletedSceneUndoTimer = window.setTimeout(() => toast.remove(), 8_000);
  }

  private buildLocationThumb(scene: StudioScene): HTMLElement {
    const thumb = document.createElement("button");
    thumb.type = "button";
    thumb.className = "fd-loc-thumb";
    thumb.dataset.kokoAction = "script-open-location";
    const location = scene.locationId
      ? this.store.get("locations").find((l) => l.id === scene.locationId) ?? null
      : null;
    if (location) {
      thumb.dataset.id = location.id;
      const image = location.panoramaImage || location.referenceImage || null;
      if (image) {
        thumb.classList.add("is-filled");
        thumb.style.backgroundImage = `url("${image}")`;
      } else {
        thumb.classList.add("is-filled", "is-placeholder");
        thumb.style.setProperty("--loc-hue", String(hashHue(location.id)));
      }
      thumb.title = `${location.name} — click to edit the location`;
      thumb.addEventListener("click", () => this.openEntityDrawerFor("location", location.id));
    } else {
      thumb.textContent = "img";
      thumb.title = "No location set for this scene";
    }
    return thumb;
  }

  private buildCharacterCircle(character: StudioCharacter): HTMLElement {
    const wrap = document.createElement("button");
    wrap.type = "button";
    wrap.className = "fd-char-circle";
    wrap.title = `Edit ${character.name}`;
    wrap.dataset.kokoAction = "open-character";
    wrap.dataset.id = character.id;
    const image = character.referenceImage;
    if (image) {
      const img = document.createElement("img");
      img.src = image;
      img.alt = character.name;
      img.draggable = false;
      wrap.appendChild(img);
    } else {
      wrap.appendChild(
        buildPixelIdenticonImage(character.id, { alt: character.name, pixelSize: 56 }),
      );
    }
    const badge = document.createElement("span");
    badge.className = "fd-edit-badge";
    badge.textContent = "✎";
    wrap.appendChild(badge);
    wrap.addEventListener("click", () => this.openEntityDrawerFor("character", character.id));
    return wrap;
  }

  private buildPlayButton(el: ScriptElement, speaker: StudioCharacter | null): HTMLElement {
    const play = document.createElement("button");
    play.type = "button";
    play.className = "fd-play";
    play.textContent = "▶";
    play.title = speaker ? `Play in ${speaker.name}'s voice` : "Play this line";
    play.dataset.kokoAction = "script-line-play";
    play.dataset.elementId = el.id;
    play.addEventListener("click", () => this.playLine(play, el, speaker));
    return play;
  }

  private buildSoundButton(el: ScriptElement, speaker: StudioCharacter | null): HTMLElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "fd-sound";
    button.textContent = "♪";
    const sound = this.lineSoundStore.get(el.id);
    if (sound && (sound.delivery !== "neutral" || sound.tone || (sound.speed ?? 1) !== 1)) {
      button.classList.add("is-set");
    }
    button.title = "Edit this line's sound (delivery, tone, speed)";
    button.dataset.kokoAction = "script-line-sound";
    button.dataset.elementId = el.id;
    button.addEventListener("click", () => this.openSoundEditor(el, speaker));
    return button;
  }

  private buildCommentMarker(el: ScriptElement): HTMLElement | null {
    if (!this.showComments) return null;
    const threads = this.commentStore.listForElement(el.id).filter((comment) => !comment.resolved);
    if (threads.length === 0) return null;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "fd-comment-marker";
    button.textContent = threads.length > 1 ? String(threads.length) : "";
    button.title = `${threads.length} comment${threads.length === 1 ? "" : "s"}`;
    button.dataset.kokoAction = "script-comment-marker";
    button.dataset.elementId = el.id;
    button.addEventListener("click", () => this.openCommentThread(threads[0].id));
    return button;
  }

  private buildStoryboardStrip(scene: StudioScene, sceneNumber: number): HTMLElement {
    const strip = document.createElement("div");
    strip.className = "fd-storyboard-strip";
    const frames = this.store.get("storyboardFramesBySceneId")[scene.id] ?? [];
    const ready = frames.filter((f) => f.image);
    if (ready.length > 0) {
      ready.slice(0, 3).forEach((frame, i) => {
        const el = document.createElement("div");
        el.className = "fd-sb-frame is-ready";
        el.style.backgroundImage = `url("${frame.image}")`;
        const cap = document.createElement("span");
        cap.className = "fd-sb-cap";
        cap.textContent = frame.caption || `shot ${i + 1}`;
        el.appendChild(cap);
        strip.appendChild(el);
      });
    } else if (scene.storyboardReady || scene.videoReady) {
      const frame = document.createElement("div");
      frame.className = "fd-sb-frame is-ready is-placeholder";
      frame.style.setProperty("--sb-hue", String(hashHue(scene.id)));
      const cap = document.createElement("span");
      cap.className = "fd-sb-cap";
      cap.textContent = `shot ${sceneNumber}`;
      frame.appendChild(cap);
      strip.appendChild(frame);
    } else {
      const empty = document.createElement("div");
      empty.className = "fd-sb-frame is-empty";
      empty.textContent = "no storyboard yet";
      strip.appendChild(empty);
    }
    return strip;
  }

  // ------------------------------------------------------------------
  // Text rendering — @-ref pills (display when not focused)
  // ------------------------------------------------------------------

  private renderTextDisplay(textEl: HTMLElement, el: ScriptElement): void {
    if (this.focusedElementId === el.id) {
      textEl.textContent = el.text;
      return;
    }
    const { byName } = this.buildEntityIndex();
    const names = Array.from(byName.values()).map((e) => e.name);
    const tokens = extractRefTokens(el.text, names);
    if (tokens.length === 0) {
      this.appendCommentAwareText(textEl, el, el.text, 0);
      return;
    }
    textEl.replaceChildren();
    let cursor = 0;
    for (const token of tokens) {
      if (token.start > cursor) {
        this.appendCommentAwareText(textEl, el, el.text.slice(cursor, token.start), cursor);
      }
      const entry = byName.get(token.entityName.trim().toLowerCase()) ?? null;
      const pill = document.createElement("span");
      pill.className = "fd-ref-pill";
      pill.contentEditable = "false";
      pill.textContent = token.raw;
      if (entry) {
        pill.dataset.refType = entry.type;
        pill.dataset.entityId = entry.id;
        pill.classList.add(`is-${entry.type}`);
        pill.title = `${entry.type}: ${entry.name} — click to edit`;
        pill.addEventListener("mousedown", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        pill.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (entry.type === "look") return;
          this.openEntityDrawerFor(entry.type, entry.id);
        });
      } else {
        pill.classList.add("is-unknown");
      }
      textEl.appendChild(pill);
      cursor = token.end;
    }
    if (cursor < el.text.length) {
      this.appendCommentAwareText(textEl, el, el.text.slice(cursor), cursor);
    }
  }

  private appendCommentAwareText(
    target: HTMLElement,
    element: ScriptElement,
    value: string,
    baseOffset: number,
  ): void {
    const comments = this.showComments
      ? this.commentStore
          .listForElement(element.id)
          .filter((comment) => !comment.resolved && !comment.detached)
      : [];
    if (comments.length === 0 || value.length === 0) {
      target.appendChild(document.createTextNode(value));
      return;
    }
    const endOffset = baseOffset + value.length;
    const boundaries = new Set<number>([baseOffset, endOffset]);
    comments.forEach((comment) => {
      if (comment.end <= baseOffset || comment.start >= endOffset) return;
      boundaries.add(Math.max(baseOffset, comment.start));
      boundaries.add(Math.min(endOffset, comment.end));
    });
    const sorted = [...boundaries].sort((a, b) => a - b);
    for (let index = 0; index < sorted.length - 1; index += 1) {
      const start = sorted[index];
      const end = sorted[index + 1];
      const text = value.slice(start - baseOffset, end - baseOffset);
      const active = comments.find((comment) => comment.start < end && comment.end > start);
      if (!active) {
        target.appendChild(document.createTextNode(text));
        continue;
      }
      const mark = document.createElement("mark");
      mark.className = "fd-comment-highlight";
      if (active.id === this.activeCommentId) mark.classList.add("is-active");
      mark.dataset.commentId = active.id;
      mark.textContent = text;
      mark.addEventListener("mousedown", (event) => event.preventDefault());
      mark.addEventListener("click", () => {
        const row = mark.closest<HTMLElement>(".fd-line");
        const textElement = mark.closest<HTMLElement>(".fd-text");
        const scene = this.store.get("scenes").find((item) => item.id === row?.dataset.sceneId);
        const index = Number(row?.dataset.lineIndex ?? -1);
        if (scene && textElement && index >= 0) {
          this.commitElement(scene, index, textElement.textContent ?? "");
        }
        this.openCommentThread(active.id);
      });
      target.appendChild(mark);
    }
  }

  // ------------------------------------------------------------------
  // Text events
  // ------------------------------------------------------------------

  private onTextFocus(textEl: HTMLElement, el: ScriptElement): void {
    this.focusedElementId = el.id;
    if (textEl.textContent !== el.text || textEl.childElementCount > 0) {
      const caret = el.text.length;
      textEl.textContent = el.text;
      setCaretOffset(textEl, caret);
    }
  }

  private onTextInput(textEl: HTMLElement, scene: StudioScene, index: number): void {
    this.updateRefMenu(textEl, scene, index);
  }

  private onTextBlur(textEl: HTMLElement, scene: StudioScene, index: number): void {
    // Defer so a click on the ref-menu can fire first.
    window.setTimeout(() => {
      if (Date.now() < this.ignoreBlurUntil) return;
      if (this.refMenu && this.refMenu.textEl === textEl) return;
      if (this.focusedElementId) this.focusedElementId = null;
      this.commitElement(scene, index, textEl.textContent ?? "");
    }, 0);
  }

  private onTextKeydown(
    event: KeyboardEvent,
    textEl: HTMLElement,
    scene: StudioScene,
    elements: ScriptElement[],
    index: number,
  ): void {
    // Ref-menu navigation takes precedence.
    if (this.refMenu && this.refMenu.textEl === textEl) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.moveRefSelection(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        this.moveRefSelection(-1);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        this.applyRefSelection();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        this.hideRefMenu();
        return;
      }
    }

    const modifier = event.metaKey || event.ctrlKey;
    if (modifier && /^[1-6]$/.test(event.key)) {
      event.preventDefault();
      const types: ElementType[] = [
        "slug",
        "action",
        "character",
        "dialogue",
        "parenthetical",
        "transition",
      ];
      const forced = types[Number(event.key) - 1];
      this.pinnedTypes.add(elements[index].id);
      this.focusedElementId = null;
      this.pendingFocus = { sceneId: scene.id, index };
      this.commitElement(scene, index, textEl.textContent ?? "", forced);
      return;
    }
    if (modifier && event.key === "Enter") {
      event.preventDefault();
      this.pendingFocus = { sceneId: scene.id, index };
      this.commitElement(scene, index, textEl.textContent ?? "");
      this.formatStore.togglePageBreak(elements[index].id);
      return;
    }
    if (modifier && event.key.toLowerCase() === "d") {
      const cue = this.findDialogueCue(elements, index);
      if (cue) {
        event.preventDefault();
        event.stopPropagation();
        this.pendingFocus = { sceneId: scene.id, index };
        this.commitElement(scene, index, textEl.textContent ?? "");
        this.formatStore.toggleDual(cue.id);
      }
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      this.focusedElementId = null;
      this.commitElement(scene, index, textEl.textContent ?? "");
      this.addLineAfter(scene, index);
      return;
    }
    if (event.key === "Tab") {
      event.preventDefault();
      const el = elements[index];
      let next = cycleType(el.type);
      if (event.shiftKey) {
        for (let step = 0; step < 4; step += 1) next = cycleType(next);
      }
      this.pinnedTypes.add(el.id);
      this.focusedElementId = null;
      this.pendingFocus = { sceneId: scene.id, index };
      this.commitElement(scene, index, textEl.textContent ?? "", next);
      return;
    }
    if (event.key === "Backspace") {
      const caret = getCaretOffset(textEl);
      if (caret === 0 && (textEl.textContent ?? "").length === 0 && index > 0) {
        event.preventDefault();
        this.removeLine(scene, index);
      }
    }
  }

  // ------------------------------------------------------------------
  // @-autocomplete
  // ------------------------------------------------------------------

  private updateRefMenu(textEl: HTMLElement, scene: StudioScene, index: number): void {
    const text = textEl.textContent ?? "";
    const caret = getCaretOffset(textEl);
    let at = -1;
    for (let i = caret - 1; i >= 0; i -= 1) {
      const ch = text[i];
      if (ch === "@") {
        const before = text[i - 1];
        if (before === undefined || /\s/.test(before)) at = i;
        break;
      }
      if (/\s/.test(ch)) break;
    }
    if (at === -1) {
      this.updateSmartTypeMenu(textEl, scene, index, text.slice(0, caret));
      return;
    }
    const query = text.slice(at + 1, caret);
    if (/[\s@]/.test(query)) {
      this.updateSmartTypeMenu(textEl, scene, index, text.slice(0, caret));
      return;
    }
    const items = this.buildRefMenuItems(query);
    this.refMenu = { mode: "ref", textEl, scene, index, tokenStart: at, query, items, selected: 0 };
    this.renderRefMenu(textEl);
  }

  private updateSmartTypeMenu(
    textEl: HTMLElement,
    scene: StudioScene,
    index: number,
    rawQuery: string,
  ): void {
    const element = this.getWorkingElements(scene)[index];
    const query = rawQuery.trim();
    let items: RefMenuItem[] = [];
    if (element?.type === "character" && query) {
      items = this.store
        .get("characters")
        .filter((character) => character.name.toLowerCase().startsWith(query.toLowerCase()))
        .slice(0, 6)
        .map((character) => ({
          label: character.name.toUpperCase(),
          hint: "character",
          entry: { type: "character", id: character.id, name: character.name },
          name: character.name.toUpperCase(),
        }));
    } else if (element?.type === "slug" && query) {
      const prefix = /^EXT\.?\b/i.test(query) ? "EXT." : "INT.";
      const locationQuery = query.replace(/^(INT|EXT|INT\/EXT)\.?\s*/i, "");
      items = this.store
        .get("locations")
        .filter((location) =>
          location.name.toLowerCase().startsWith(locationQuery.toLowerCase()),
        )
        .slice(0, 6)
        .map((location) => ({
          label: `${prefix} ${location.name.toUpperCase()} - DAY`,
          hint: "scene",
          entry: { type: "location", id: location.id, name: location.name },
          name: `${prefix} ${location.name.toUpperCase()} - DAY`,
        }));
    } else if (element?.type === "transition" && query) {
      items = ["CUT TO:", "DISSOLVE TO:", "SMASH CUT TO:", "FADE OUT."]
        .filter((value) => value.startsWith(query.toUpperCase()))
        .map((value) => ({ label: value, hint: "transition", entry: null, name: value }));
    }
    if (items.length === 0) {
      this.hideRefMenu();
      return;
    }
    this.refMenu = {
      mode: "smart",
      textEl,
      scene,
      index,
      tokenStart: 0,
      query,
      items,
      selected: 0,
    };
    this.renderRefMenu(textEl);
  }

  private buildRefMenuItems(query: string): RefMenuItem[] {
    const { list } = this.buildEntityIndex();
    const q = query.trim().toLowerCase();
    const matches = list
      .filter((e) => !q || e.name.toLowerCase().includes(q))
      .slice(0, 6)
      .map<RefMenuItem>((entry) => ({
        label: entry.name,
        hint: entry.type,
        entry,
        name: entry.name,
      }));
    const items = [...matches];
    if (q) {
      const exists = list.some((e) => e.name.toLowerCase() === q);
      if (!exists) {
        const display = query.trim();
        items.push(
          { label: `Create character “${display}”`, hint: "new", entry: null, createKind: "character", name: display },
          { label: `Create location “${display}”`, hint: "new", entry: null, createKind: "location", name: display },
          { label: `Create asset “${display}”`, hint: "new", entry: null, createKind: "asset", name: display },
        );
      }
    }
    return items;
  }

  private renderRefMenu(textEl: HTMLElement): void {
    const menu = this.refMenuEl;
    if (!menu || !this.refMenu) return;
    if (this.refMenu.items.length === 0) {
      this.hideRefMenu();
      return;
    }
    menu.replaceChildren();
    this.refMenu.items.forEach((item, i) => {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "fd-ref-item";
      if (i === this.refMenu!.selected) row.classList.add("is-selected");
      row.dataset.kokoAction = "ref-menu-item";
      const label = document.createElement("span");
      label.className = "fd-ref-item-label";
      label.textContent = item.label;
      const hint = document.createElement("span");
      hint.className = "fd-ref-item-hint";
      hint.textContent = item.hint;
      row.append(label, hint);
      row.addEventListener("mousedown", (e) => e.preventDefault());
      row.addEventListener("click", () => {
        this.refMenu!.selected = i;
        this.applyRefSelection();
      });
      menu.appendChild(row);
    });
    const rect = textEl.getBoundingClientRect();
    menu.style.left = `${Math.round(rect.left)}px`;
    menu.style.top = `${Math.round(rect.bottom + 4)}px`;
    menu.hidden = false;
  }

  private moveRefSelection(delta: number): void {
    if (!this.refMenu) return;
    const n = this.refMenu.items.length;
    this.refMenu.selected = (this.refMenu.selected + delta + n) % n;
    this.renderRefMenu(this.refMenu.textEl);
  }

  private applyRefSelection(): void {
    if (!this.refMenu) return;
    const { mode, textEl, scene, index, tokenStart, items, selected } = this.refMenu;
    const item = items[selected];
    if (!item) {
      this.hideRefMenu();
      return;
    }
    if (mode === "smart") {
      textEl.textContent = item.name;
      setCaretOffset(textEl, item.name.length);
      this.hideRefMenu();
      this.commitElement(scene, index, item.name, undefined, { keepFocus: true });
      return;
    }
    let name = item.name;
    if (!item.entry && item.createKind) {
      const created = this.autoCreateEntity(item.createKind, name);
      name = created.name;
    } else if (item.entry) {
      name = item.entry.name;
    }
    const text = textEl.textContent ?? "";
    const caret = getCaretOffset(textEl);
    const before = text.slice(0, tokenStart);
    const after = text.slice(caret);
    const insert = `@${name}`;
    const sep = after.startsWith(" ") || after === "" ? "" : " ";
    const next = `${before}${insert}${sep}${after}`;
    textEl.textContent = next;
    setCaretOffset(textEl, before.length + insert.length + 1);
    this.hideRefMenu();
    this.commitElement(scene, index, next, undefined, { keepFocus: true });
  }

  private hideRefMenu(): void {
    this.refMenu = null;
    if (this.refMenuEl) this.refMenuEl.hidden = true;
  }

  // ------------------------------------------------------------------
  // Commit / living-document mutations
  // ------------------------------------------------------------------

  private getWorkingElements(scene: StudioScene): ScriptElement[] {
    const current = this.store.get("scenes").find((s) => s.id === scene.id);
    if (!current) return [];
    const els = resolveSceneElements(current, this.store.get("characters"));
    if (els.length === 0) {
      els.push({ id: makeElementId(), type: "action", text: "", characterId: null });
    }
    return els;
  }

  private commitElement(
    scene: StudioScene,
    index: number,
    rawText: string,
    forcedType?: ElementType,
    options: { keepFocus?: boolean } = {},
  ): void {
    const beforeScenes = this.cloneScenes();
    if (options.keepFocus) this.pendingFocus = { sceneId: scene.id, index };
    const els = this.getWorkingElements(scene);
    if (index < 0 || index >= els.length) return;
    const el = els[index];
    const prevType = index > 0 ? els[index - 1].type : null;
    const characters = this.store.get("characters");

    let type: ElementType;
    if (forcedType) {
      type = forcedType;
    } else if (rawText === el.text) {
      type = el.type;
    } else if (rawText.length === 0) {
      // A newly-created blank line keeps its SmartType/Enter-assigned element
      // while focus moves between renders. Classification resumes once typed.
      type = el.type;
    } else {
      const known = !!findCharacterByName(rawText.trim(), characters);
      const pinned = this.pinnedTypes.has(el.id) ? el.type : null;
      type = classifyType(rawText, prevType, known, pinned);
    }

    const textChanged = rawText !== el.text;
    const typeChanged = type !== el.type;

    // Living-doc: a character cue creates the character; a slug creates
    // the location and links it to the scene.
    let characterId: string | null = null;
    let locationPatch: { locationId?: string | null } = {};
    if (type === "character") {
      const name = rawText.trim();
      if (name) characterId = this.store.autoCreateCharacter(name).id;
    }
    if (type === "slug") {
      const parsed = parseSlug(rawText);
      if (parsed) {
        const location = this.store.autoCreateLocation(parsed.locationName, {
          intExt: parsed.intExt ?? undefined,
          timeOfDay: parsed.timeOfDay ? [parsed.timeOfDay] : undefined,
        });
        locationPatch = { locationId: location.id };
      }
    }

    els[index] = { ...el, text: rawText, type, characterId };

    if (textChanged || typeChanged || Object.keys(locationPatch).length > 0) {
      if (textChanged) this.commentStore.rebaseElement(el.id, el.text, rawText);
      this.store.updateScene(scene.id, elementsToBody(els), {
        elements: els,
        ...locationPatch,
        ...(textChanged
          ? { storyboardReady: false, videoReady: false, generatedVoiceLine: false }
          : {}),
      });
      this.pushHistory("Edit screenplay", beforeScenes, this.cloneScenes());
    }

    this.syncSceneRefs(scene.id, els);

  }

  private syncSceneRefs(sceneId: string, els: ScriptElement[]): void {
    const { byName } = this.buildEntityIndex();
    const names = Array.from(byName.values()).map((e) => e.name);
    const refs: Array<{
      type: "character" | "location" | "look" | "asset";
      entityId: string;
      elementId: string;
      column: number;
    }> = [];
    for (const el of els) {
      const tokens = extractRefTokens(el.text, names);
      for (const token of tokens) {
        const key = token.entityName.trim().toLowerCase();
        let entry = byName.get(key);
        if (!entry) {
          const created = this.store.autoCreateAsset(token.entityName.trim());
          entry = { type: "asset", id: created.id, name: created.name };
          byName.set(key, entry);
          names.push(created.name);
        }
        refs.push({
          type: entry.type,
          entityId: entry.id,
          elementId: el.id,
          column: token.start,
        });
      }
    }
    this.store.replaceSceneRefs(sceneId, refs);
  }

  private setElementType(
    scene: StudioScene,
    index: number,
    type: ElementType,
    liveText?: string,
  ): void {
    const els = this.getWorkingElements(scene);
    if (index < 0 || index >= els.length) return;
    this.pendingFocus = { sceneId: scene.id, index };
    this.commitElement(scene, index, liveText ?? els[index].text, type);
  }

  private addLineAfter(scene: StudioScene, index: number): void {
    const current = this.getWorkingElements(scene)[index];
    if (current?.type === "transition") {
      this.startNewScene("", "slug");
      return;
    }
    this.runHistoryAction("Insert screenplay element", () => {
      const els = this.getWorkingElements(scene);
      const at = Math.min(index + 1, els.length);
      const prevType = els[index]?.type ?? "action";
      const newEl: ScriptElement = {
        id: makeElementId(),
        type: nextTypeOnEnter(prevType),
        text: "",
        characterId: null,
      };
      els.splice(at, 0, newEl);
      this.pendingFocus = { sceneId: scene.id, index: at };
      this.store.updateScene(scene.id, elementsToBody(els), { elements: els });
    });
  }

  private removeLine(scene: StudioScene, index: number): void {
    const els = this.getWorkingElements(scene);
    if (els.length <= 1 || index <= 0) return;
    const removed = els[index];
    this.runHistoryAction("Delete screenplay element", () => {
      els.splice(index, 1);
      this.commentStore.detachElement(removed.id);
      this.pendingFocus = { sceneId: scene.id, index: Math.max(0, index - 1) };
      this.store.updateScene(scene.id, elementsToBody(els), { elements: els });
    });
  }

  private startNewScene(initial = "", initialType?: ElementType): void {
    this.runHistoryAction("Create scene", () => {
      const text = initial.trim();
      const scene = this.store.addScene(text);
      const els = bodyToElements(text || "", this.store.get("characters"));
      const first = els[0];
      if (first) {
        if (initialType) first.type = initialType;
        let locationId: string | null = null;
        if (first.type === "character" && first.text.trim()) {
          first.characterId = this.store.autoCreateCharacter(first.text.trim()).id;
        }
        if (first.type === "slug") {
          const parsed = parseSlug(first.text);
          if (parsed) {
            locationId = this.store.autoCreateLocation(parsed.locationName, {
              intExt: parsed.intExt ?? undefined,
            }).id;
          }
        }
        this.pendingFocus = { sceneId: scene.id, index: Math.max(0, els.length - 1) };
        this.store.updateScene(scene.id, elementsToBody(els), {
          elements: els,
          ...(locationId ? { locationId } : {}),
        });
      }
    });
  }

  // ------------------------------------------------------------------
  // Document history
  // ------------------------------------------------------------------

  private cloneScenes(): StudioScene[] {
    return JSON.parse(JSON.stringify(this.store.get("scenes"))) as StudioScene[];
  }

  private runHistoryAction(label: string, action: () => unknown): void {
    const before = this.cloneScenes();
    action();
    this.pushHistory(label, before, this.cloneScenes());
  }

  private pushHistory(label: string, before: StudioScene[], after: StudioScene[]): void {
    if (this.restoringHistory || JSON.stringify(before) === JSON.stringify(after)) return;
    this.history = this.history.slice(0, this.historyIndex + 1);
    this.history.push({ label, before, after });
    if (this.history.length > 100) this.history.shift();
    this.historyIndex = this.history.length - 1;
    this.updateHistoryButtons();
  }

  private undo(): void {
    if (this.historyIndex < 0) return;
    const entry = this.history[this.historyIndex];
    this.historyIndex -= 1;
    this.restoreScenes(entry.before);
  }

  private redo(): void {
    if (this.historyIndex + 1 >= this.history.length) return;
    this.historyIndex += 1;
    this.restoreScenes(this.history[this.historyIndex].after);
  }

  private restoreScenes(scenes: StudioScene[]): void {
    this.restoringHistory = true;
    this.ignoreBlurUntil = Date.now() + 100;
    try {
      if (scenes.length > 0) {
        this.store.setScript(this.store.get("scriptFilename") ?? "Untitled screenplay", scenes);
      } else {
        for (const scene of [...this.store.get("scenes")]) this.store.deleteScene(scene.id);
      }
    } finally {
      this.restoringHistory = false;
      this.updateHistoryButtons();
    }
  }

  private updateHistoryButtons(): void {
    const undo = this.rootEl?.querySelector<HTMLButtonElement>(".fd-undo");
    const redo = this.rootEl?.querySelector<HTMLButtonElement>(".fd-redo");
    if (undo) undo.disabled = this.historyIndex < 0;
    if (redo) redo.disabled = this.historyIndex + 1 >= this.history.length;
  }

  // ------------------------------------------------------------------
  // Focus management
  // ------------------------------------------------------------------

  private applyPendingFocus(): void {
    const target = this.pendingFocus;
    this.pendingFocus = null;
    if (!target || !this.pageEl) return;
    // Let blur commits from the replaced contenteditable finish first; then
    // focus the latest rendered node, not the node that initiated the edit.
    window.setTimeout(() => {
      requestAnimationFrame(() => {
        const el = this.pageEl?.querySelector<HTMLElement>(
          `.fd-text[data-scene-id="${cssEscape(target.sceneId)}"][data-line-index="${target.index}"]`,
        );
        if (el) {
          this.focusedElementId = el.dataset.elementId ?? null;
          el.focus();
          setCaretOffset(el, (el.textContent ?? "").length);
        }
      });
    }, 0);
  }

  // ------------------------------------------------------------------
  // Per-line voice + sound (G8)
  // ------------------------------------------------------------------

  private resolveSpeaker(elements: ScriptElement[], index: number): StudioCharacter | null {
    for (let i = index; i >= 0; i -= 1) {
      if (elements[i].type === "character" && elements[i].characterId) {
        return (
          this.store.get("characters").find((c) => c.id === elements[i].characterId) ?? null
        );
      }
      if (elements[i].type === "slug") break;
    }
    return null;
  }

  private async playLine(
    button: HTMLButtonElement,
    el: ScriptElement,
    speaker: StudioCharacter | null,
  ): Promise<void> {
    const text = el.text.trim();
    if (!text) return;
    const sound = this.lineSoundStore.get(el.id);
    const voice = resolveVoice(speaker, sound);
    button.classList.add("is-playing");
    await this.voicePlayer
      .speak(text, voice, { onEnd: () => button.classList.remove("is-playing") })
      .catch(() => button.classList.remove("is-playing"));
  }

  private openSoundEditor(el: ScriptElement, speaker: StudioCharacter | null): void {
    const current = this.lineSoundStore.get(el.id) ?? { ...DEFAULT_LINE_SOUND };
    openEntityDrawer({
      title: "Line sound",
      subtitle: speaker ? `${speaker.name}'s voice` : "Dialogue delivery",
      render: (body) => {
        const note = document.createElement("p");
        note.className = "studio-helper";
        note.textContent = speaker
          ? `Tune how this line is performed in ${speaker.name}'s voice. ▶ uses these settings.`
          : "Tune how this line is performed. ▶ uses these settings.";
        body.appendChild(note);

        const draft: LineSound = { ...current };

        const deliveryRow = document.createElement("div");
        deliveryRow.className = "fd-sound-deliveries";
        const deliveries: Delivery[] = ["neutral", "whisper", "warm", "shout"];
        const buttons: HTMLButtonElement[] = [];
        for (const d of deliveries) {
          const b = document.createElement("button");
          b.type = "button";
          b.className = "fd-sound-delivery";
          b.textContent = d;
          b.dataset.delivery = d;
          if (draft.delivery === d) b.classList.add("is-active");
          b.addEventListener("click", () => {
            draft.delivery = d;
            buttons.forEach((x) => x.classList.toggle("is-active", x === b));
          });
          buttons.push(b);
          deliveryRow.appendChild(b);
        }
        body.appendChild(labeled("Delivery", deliveryRow));

        const tone = document.createElement("input");
        tone.type = "text";
        tone.className = "studio-input fd-sound-tone";
        tone.placeholder = "e.g. bitter, playful, exhausted";
        tone.value = draft.tone ?? "";
        tone.addEventListener("input", () => (draft.tone = tone.value));
        body.appendChild(labeled("Tone", tone));

        const speed = document.createElement("input");
        speed.type = "range";
        speed.min = "0.7";
        speed.max = "1.4";
        speed.step = "0.05";
        speed.value = String(draft.speed ?? 1);
        speed.className = "fd-sound-speed";
        const speedVal = document.createElement("span");
        speedVal.className = "fd-sound-speed-val";
        speedVal.textContent = `${Number(speed.value).toFixed(2)}×`;
        speed.addEventListener("input", () => {
          draft.speed = Number(speed.value);
          speedVal.textContent = `${Number(speed.value).toFixed(2)}×`;
        });
        const speedWrap = document.createElement("div");
        speedWrap.className = "fd-sound-speed-row";
        speedWrap.append(speed, speedVal);
        body.appendChild(labeled("Speed", speedWrap));

        const actions = document.createElement("div");
        actions.className = "fd-sound-actions";
        const preview = buildStudioButton("▶ Preview", {
          onClick: () => {
            const voice = resolveVoice(speaker, draft);
            void this.voicePlayer.speak(el.text, voice);
          },
        });
        preview.dataset.kokoAction = "script-sound-preview";
        const save = buildStudioButton("Save", {
          variant: "primary",
          onClick: () => {
            this.lineSoundStore.set(el.id, draft);
            closeEntityDrawer();
          },
        });
        save.dataset.kokoAction = "script-sound-save";
        actions.append(preview, save);
        body.appendChild(actions);
      },
    });
  }

  // ------------------------------------------------------------------
  // Comments (G-Docs style margin threads)
  // ------------------------------------------------------------------

  private toggleComments(): void {
    this.showComments = !this.showComments;
    if (!this.showComments) {
      this.activeCommentId = null;
      this.commentSelection = null;
      this.hideSelectionButton();
    }
    this.applyCommentsClass();
    this.render();
  }

  private applyCommentsClass(): void {
    this.rootEl?.classList.toggle("fd-comments-on", this.showComments);
    const toggle = this.rootEl?.querySelector<HTMLElement>(".fd-comments-toggle");
    toggle?.classList.toggle("is-on", this.showComments);
    if (toggle) {
      toggle.textContent = this.showComments ? "Comments on" : "Comments off";
      toggle.setAttribute("aria-pressed", String(this.showComments));
    }
  }

  private handleKokoComment(event: CustomEvent): void {
    const detail = (event.detail ?? {}) as {
      sceneId?: string;
      elementId?: string;
      text?: string;
    };
    if (!detail.elementId || !detail.text) return;
    const scene = this.store
      .get("scenes")
      .find((s) => (s.elements ?? []).some((el) => el.id === detail.elementId));
    const sceneId = detail.sceneId ?? scene?.id ?? "";
    const quote = scene?.elements?.find((el) => el.id === detail.elementId)?.text ?? "";
    this.commentStore.add({
      sceneId,
      elementId: detail.elementId,
      quote,
      start: 0,
      end: quote.length,
      text: detail.text,
      author: "koko",
    });
  }

  private captureCommentSelection(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      this.hideSelectionButton();
      return;
    }
    const range = selection.getRangeAt(0);
    const startElement = closestTextElement(range.startContainer);
    const endElement = closestTextElement(range.endContainer);
    if (!startElement || startElement !== endElement || !this.pageEl?.contains(startElement)) {
      this.hideSelectionButton();
      return;
    }
    const quote = range.toString();
    if (!quote.trim()) {
      this.hideSelectionButton();
      return;
    }
    const start = offsetWithin(startElement, range.startContainer, range.startOffset);
    const end = offsetWithin(startElement, range.endContainer, range.endOffset);
    const rect = range.getBoundingClientRect();
    this.commentSelection = {
      sceneId: startElement.dataset.sceneId ?? "",
      elementId: startElement.dataset.elementId ?? "",
      start: Math.min(start, end),
      end: Math.max(start, end),
      quote,
      rect,
    };
    if (this.selectionButtonEl) {
      this.selectionButtonEl.hidden = false;
      this.selectionButtonEl.style.left = `${Math.round(rect.right + 8)}px`;
      this.selectionButtonEl.style.top = `${Math.round(rect.top - 34)}px`;
    }
  }

  private hideSelectionButton(): void {
    if (this.selectionButtonEl) this.selectionButtonEl.hidden = true;
  }

  private openSelectionComposer(): void {
    if (!this.commentSelection) return;
    this.showComments = true;
    this.activeCommentId = null;
    this.focusedElementId = null;
    this.hideSelectionButton();
    this.applyCommentsClass();
    this.render();
  }

  private openCommentThread(commentId: string): void {
    this.showComments = true;
    this.activeCommentId = commentId;
    this.commentSelection = null;
    this.applyCommentsClass();
    this.render();
  }

  private buildNewCommentComposer(selection: CommentSelection): HTMLElement {
    const form = document.createElement("div");
    form.className = "fd-comment-form";
    const input = document.createElement("textarea");
    input.className = "fd-comment-input studio-input";
    input.placeholder = "Add a comment…";
    input.rows = 2;
    input.dataset.kokoAction = "script-comment-input";
    const actions = document.createElement("div");
    actions.className = "fd-comment-form-actions";
    const submit = buildStudioButton("Comment", {
      variant: "primary",
      onClick: () => {
        const text = input.value.trim();
        if (!text) return;
        const added = this.commentStore.add({
          sceneId: selection.sceneId,
          elementId: selection.elementId,
          quote: selection.quote,
          start: selection.start,
          end: selection.end,
          text,
        });
        this.activeCommentId = added.id;
        this.commentSelection = null;
        this.render();
      },
    });
    submit.dataset.kokoAction = "script-comment-submit";
    const cancel = buildStudioButton("Cancel", {
      onClick: () => {
        this.commentSelection = null;
        this.renderComments();
      },
    });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        submit.click();
      }
    });
    actions.append(submit, cancel);
    form.append(input, actions);
    return form;
  }

  private renderComments(): void {
    if (!this.marginEl) return;
    this.marginEl.replaceChildren();
    if (!this.showComments) return;
    if (this.commentSelection) {
      const thread = document.createElement("div");
      thread.className = "fd-comment-thread fd-comment-new";
      thread.dataset.elementId = this.commentSelection.elementId;
      const quote = document.createElement("div");
      quote.className = "fd-comment-quote";
      quote.textContent = this.commentSelection.quote;
      thread.append(quote, this.buildNewCommentComposer(this.commentSelection));
      this.marginEl.appendChild(thread);
      this.alignCommentThread(this.commentSelection.elementId, thread);
      requestAnimationFrame(() => thread.querySelector<HTMLTextAreaElement>("textarea")?.focus());
      return;
    }
    const active = this.activeCommentId ? this.commentStore.get(this.activeCommentId) : null;
    if (!active || active.resolved) return;
    const thread = this.buildCommentThread(active);
    this.marginEl.appendChild(thread);
    this.alignCommentThread(active.elementId, thread);
  }

  private alignCommentThread(elementId: string, thread: HTMLElement): void {
    requestAnimationFrame(() => {
      const anchor = this.pageEl?.querySelector<HTMLElement>(
        `.fd-line[data-element-id="${cssEscape(elementId)}"]`,
      );
      const layout = this.rootEl?.querySelector<HTMLElement>(".fd-layout");
      if (!anchor || !layout) return;
      const top = Math.max(0, anchor.getBoundingClientRect().top - layout.getBoundingClientRect().top);
      thread.style.marginTop = `${Math.round(top)}px`;
    });
  }

  private buildCommentThread(comment: ScriptComment): HTMLElement {
    const thread = document.createElement("div");
    thread.className = "fd-comment-thread";
    thread.dataset.elementId = comment.elementId;
    thread.dataset.commentId = comment.id;

    const quote = document.createElement("div");
    quote.className = "fd-comment-quote";
    quote.textContent = comment.detached ? `Detached: “${comment.quote}”` : comment.quote;
    thread.appendChild(quote);
    thread.appendChild(this.buildCommentMessage(comment.author, comment.text));
    comment.replies.forEach((reply) =>
      thread.appendChild(this.buildCommentMessage(reply.author, reply.text)),
    );

    const input = document.createElement("textarea");
    input.className = "fd-comment-reply studio-input";
    input.rows = 1;
    input.placeholder = "Reply…";
    input.dataset.kokoAction = "script-comment-reply-input";
    const actions = document.createElement("div");
    actions.className = "fd-comment-actions";
    const reply = buildStudioButton("Reply", {
      variant: "primary",
      onClick: () => {
        if (this.commentStore.reply(comment.id, input.value)) input.value = "";
      },
    });
    reply.dataset.kokoAction = "script-comment-reply";
    const resolve = buildStudioButton("Resolve", {
      onClick: () => {
        this.activeCommentId = null;
        this.commentStore.setResolved(comment.id, true);
        this.render();
      },
    });
    resolve.dataset.kokoAction = "script-comment-resolve";
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) reply.click();
    });
    actions.append(reply, resolve);
    thread.append(input, actions);
    return thread;
  }

  private buildCommentMessage(authorValue: "you" | "koko", value: string): HTMLElement {
    const card = document.createElement("div");
    card.className = "fd-comment-card";
    const head = document.createElement("div");
    head.className = "fd-comment-head";
    const author = document.createElement("span");
    author.className = `fd-comment-author is-${authorValue}`;
    author.textContent = authorValue === "koko" ? "Koko" : "You";
    head.appendChild(author);
    card.appendChild(head);
    const text = document.createElement("div");
    text.className = "fd-comment-text";
    text.textContent = value;
    card.appendChild(text);
    return card;
  }

  // ------------------------------------------------------------------
  // Entity edit-drawer (G6) + living-doc auto-create
  // ------------------------------------------------------------------

  private autoCreateEntity(kind: EntityKind, name: string): { id: string; name: string } {
    switch (kind) {
      case "character":
        return this.store.autoCreateCharacter(name);
      case "location":
        return this.store.autoCreateLocation(name);
      case "look":
        // No global look store — fall back to an asset so the ref resolves.
        return this.store.autoCreateAsset(name);
      default:
        return this.store.autoCreateAsset(name);
    }
  }

  private openEntityDrawerFor(kind: EntityKind, id: string): void {
    if (kind === "character") {
      const character = this.store.get("characters").find((c) => c.id === id);
      if (character) this.openCharacterDrawer(character);
    } else if (kind === "location") {
      const location = this.store.get("locations").find((l) => l.id === id);
      if (location) this.openLocationDrawer(location);
    } else if (kind === "asset") {
      const asset = this.store.get("assets").find((a) => a.id === id);
      if (asset) this.openAssetDrawer(asset);
    }
  }

  private openCharacterDrawer(character: StudioCharacter): void {
    // Reuse the Characters panel's full tabbed detail form (physical +
    // tagging + voice) in the shared drawer (G6) rather than a stub.
    const form = new CharacterDetailForm(this.store, character.id, {
      chrome: "drawer",
      onDeleted: () => closeEntityDrawer(),
    });
    openEntityDrawer({
      title: `Edit ${character.name}`,
      subtitle: "Character",
      render: (body) => body.appendChild(form.getElement()),
      onClose: () => form.destroy(),
    });
  }

  private openLocationDrawer(location: StudioLocation): void {
    openEntityDrawer({
      title: `Edit ${location.name}`,
      subtitle: "Location",
      render: (body) => {
        const draft = { name: location.name, description: location.description };
        const name = textField("Name", draft.name, (v) => (draft.name = v));
        const desc = textArea(
          "Description (materials, layout, scale)",
          draft.description,
          (v) => (draft.description = v),
        );
        body.append(name.wrap, desc.wrap);
        body.appendChild(
          this.buildImageEditRow(
            location.panoramaImage || location.referenceImage || "",
            (next) => this.store.updateLocation(location.id, { panoramaImage: next }),
          ),
        );
        const save = buildStudioButton("Save", {
          variant: "primary",
          onClick: () => {
            this.store.updateLocation(location.id, {
              name: draft.name.trim() || location.name,
              description: draft.description,
            });
            closeEntityDrawer();
          },
        });
        save.dataset.kokoAction = "drawer-save";
        body.appendChild(rowOf(save));
      },
    });
  }

  private openAssetDrawer(asset: StudioAsset): void {
    // Reuse the Assets panel's real edit form (name + physical description
    // with live attribute highlights) in the shared drawer (G6).
    openEntityDrawer({
      title: `Edit ${asset.name}`,
      subtitle: "Asset",
      render: (body) =>
        body.appendChild(
          buildAssetEditForm(this.store, asset, {
            onSaved: () => closeEntityDrawer(),
            onCancel: () => closeEntityDrawer(),
          }),
        ),
    });
  }

  private buildImageEditRow(image: string, onChange: (next: string) => void): HTMLElement {
    const row = document.createElement("div");
    row.className = "fd-drawer-image-row";
    const button = buildStudioButton(image ? "Edit image" : "Add image", {
      onClick: () => openImageEditor({ image: image || "", onChange: (next) => onChange(next) }),
    });
    button.dataset.kokoAction = "drawer-edit-image";
    row.appendChild(button);
    return row;
  }

  // ------------------------------------------------------------------
  // Inline PDF upload (empty-state)
  // ------------------------------------------------------------------

  private async handleInlineUpload(file: File): Promise<void> {
    const status = this.pageEl?.querySelector<HTMLElement>(".fd-inline-status");
    const setStatus = (cls: string, msg: string) => {
      if (!status) return;
      status.hidden = false;
      status.className = `fd-inline-status ${cls}`;
      status.textContent = msg;
    };
    if (!looksLikePdf(file)) {
      setStatus("is-error", `“${file.name}” isn't a PDF. Export your script as PDF.`);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setStatus(
        "is-error",
        `“${file.name}” is ${formatBytes(file.size)} — over the ${formatBytes(MAX_FILE_BYTES)} limit.`,
      );
      return;
    }
    try {
      setStatus("is-working", `Reading “${file.name}”…`);
      const raw = await extractPdfText(file);
      if (raw.trim().length === 0) {
        setStatus("is-error", "No text found — this looks like a scanned PDF.");
        return;
      }
      setStatus("is-working", "Koko is pulling out scenes, cast, and locations…");
      const { text } = clampText(raw);
      const parsed = await analyzeScript(text, resolveWorkerBaseUrl());
      if (countParsedScenes(parsed) === 0) {
        setStatus("is-error", "Couldn't find any scenes in that PDF.");
        return;
      }
      ingestParsedIntoStore(this.store, file.name, parsed);
      // Store change re-renders the page into the populated script.
    } catch (error) {
      console.error("[studio] inline script upload failed:", error);
      setStatus("is-error", toUserMessage(error));
    }
  }
}

// --------------------------------------------------------------------
// Small DOM + caret helpers
// --------------------------------------------------------------------

function labeled(labelText: string, control: HTMLElement): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "fd-field";
  const label = document.createElement("label");
  label.className = "fd-field-label";
  label.textContent = labelText;
  wrap.append(label, control);
  return wrap;
}

function textField(
  labelText: string,
  value: string,
  onInput: (v: string) => void,
): { wrap: HTMLElement; input: HTMLInputElement } {
  const input = document.createElement("input");
  input.type = "text";
  input.className = "studio-input";
  input.value = value;
  input.addEventListener("input", () => onInput(input.value));
  return { wrap: labeled(labelText, input), input };
}

function textArea(
  labelText: string,
  value: string,
  onInput: (v: string) => void,
): { wrap: HTMLElement; input: HTMLTextAreaElement } {
  const input = document.createElement("textarea");
  input.className = "studio-input";
  input.rows = 4;
  input.value = value;
  input.addEventListener("input", () => onInput(input.value));
  return { wrap: labeled(labelText, input), input };
}

function rowOf(...els: HTMLElement[]): HTMLElement {
  const row = document.createElement("div");
  row.className = "fd-drawer-actions";
  row.append(...els);
  return row;
}

function getCaretOffset(el: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return (el.textContent ?? "").length;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.endContainer)) return (el.textContent ?? "").length;
  const pre = range.cloneRange();
  pre.selectNodeContents(el);
  pre.setEnd(range.endContainer, range.endOffset);
  return pre.toString().length;
}

function setCaretOffset(el: HTMLElement, offset: number): void {
  const total = (el.textContent ?? "").length;
  const pos = Math.max(0, Math.min(offset, total));
  const range = document.createRange();
  const node = el.firstChild;
  if (node && node.nodeType === Node.TEXT_NODE) {
    range.setStart(node, Math.min(pos, node.textContent?.length ?? 0));
  } else {
    range.selectNodeContents(el);
    range.collapse(false);
  }
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function closestTextElement(node: Node): HTMLElement | null {
  const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  return element?.closest<HTMLElement>(".fd-text") ?? null;
}

function offsetWithin(root: HTMLElement, node: Node, offset: number): number {
  const range = document.createRange();
  range.selectNodeContents(root);
  try {
    range.setEnd(node, offset);
  } catch {
    return 0;
  }
  return range.toString().length;
}

function hashHue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

function cssEscape(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}
